'use client';

import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Button } from '~/components/Buttons';
import { Container } from '~/components/Container';
import { HeadlineSubheadCta } from '~/components/HeadlineSubheadCta';
import type { AcfMerch } from '~/types/graphql';
import { getMaskedPathSync } from '~/utils/url';

interface MerchProps extends AcfMerch {
  team?: string | null;
}

export function Merch({ merch, team }: MerchProps) {
  if (!merch) return null;

  const { headline, subhead, cta, products } = merch;

  return (
    <div className="merch mb-12 px-[--site-margin] py-5 pt-12 md:py-16">
      <Container className="gap-y-8 lg:gap-y-8">
        <HeadlineSubheadCta
          headline={headline || 'Merch'}
          subhead={subhead}
          cta={cta}
          hideCtaMobile
        />

        <Swiper
          className="w-full !pb-12 lg:!pb-0 [&_.swiper-pagination-bullet-active]:bg-black"
          slidesPerView={1}
          spaceBetween={20}
          modules={[Pagination]}
          pagination={{ clickable: true }}
          breakpoints={{
            768: {
              slidesPerView: 'auto',
              spaceBetween: 32,
            },
          }}
        >
          {products?.map((product, i) => {
            if (!product) return null;

            return (
              <SwiperSlide
                key={i}
                className="flex flex-col gap-6 gap-y-6 md:max-w-[304px]"
              >
                {product.image?.node?.sourceUrl && (
                  <a
                    href={product.link ?? undefined}
                    className="flex w-full overflow-hidden rounded"
                  >
                    <img
                      className="w-full transition-all duration-[500ms] hover:scale-[1.05]"
                      src={getMaskedPathSync(
                        product.image.node.sourceUrl,
                        team ?? undefined,
                      )}
                      alt={product.image.node.altText ?? ''}
                    />
                  </a>
                )}

                <div className="flex flex-col gap-[18px] self-stretch">
                  <a href={product.link ?? undefined} className="block">
                    <h3 className="mt-4 font-nova text-2xl font-black uppercase">
                      {product?.name}
                    </h3>
                  </a>
                  {product?.price && (
                    <a
                      href={product.link ?? undefined}
                      className="block font-roboto text-lg font-medium uppercase leading-[100%] tracking-[3.06px] text-[#7a7a7a]"
                    >
                      {product?.price}
                    </a>
                  )}
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>

        {!!cta?.url && (
          <Button isLarge href={cta.url} className="lg:hidden">
            {cta.title || 'See More'}
          </Button>
        )}
      </Container>
    </div>
  );
}
